// The specific reasons this module's backend gives, in the four languages.
//
// Two codes travel INSIDE an error and are not the code it is thrown with:
//
//   · `reasons` — what a generic refusal carries in `details.reason` or
//     `details.code` (`ConflictError(…, 'CONFLICT', { reason: 'ALREADY_CLOSED' })`).
//     errorMessage() says it from `errors.reasons.<REASON>` instead of the
//     generic «that record is no longer in the state you expected».
//   · `fields` — the code of each field of a ValidationError, said by
//     fieldErrors() from `errors.fields.<CODE>` instead of «not a valid value».
//
// Generic field codes (EMPTY, IN_THE_FUTURE, NOT_FOUND…) are the shell's and
// are not repeated here. platform/web/tests/errorReasons.test.js reads the
// backend and fails on a code without its four sentences, and on two
// modules saying one code differently (the shell merges them into one).

const errorReasons = {
  es: {
    reasons: {
      SELF_REGISTER: 'Tu propia jornada no la puedes apuntar tú: fíchala desde tu móvil o pide a otro encargado que la apunte',
      SELF_VALIDATE: 'No puedes firmar un día tuyo con entradas que apuntaste tú: tiene que firmarlo otro encargado',
      SELF_VOID: 'Tu propio día no lo puedes anular tú: tiene que hacerlo otro encargado',
      ORIGIN_TAKEN: 'Ese registro del otro sistema ya se importó',
      ORIGIN_CHANGED: 'Ese registro del otro sistema ya se importó con otros datos',
    },
    fields: {
      DUPLICATE_IN_CHAIN: 'La misma marca aparece dos veces en la cadena de correcciones',
      UNKNOWN_WORKER: 'No se encuentra a esa persona',
    },
  },
  ca: {
    reasons: {
      SELF_REGISTER: 'La teva pròpia jornada no la pots apuntar tu: fitxa-la des del mòbil o demana a un altre encarregat que l’apunti',
      SELF_VALIDATE: 'No pots signar un dia teu amb entrades que vas apuntar tu: l’ha de signar un altre encarregat',
      SELF_VOID: 'El teu propi dia no el pots anul·lar tu: ho ha de fer un altre encarregat',
      ORIGIN_TAKEN: 'Aquest registre de l’altre sistema ja es va importar',
      ORIGIN_CHANGED: 'Aquest registre de l’altre sistema ja es va importar amb altres dades',
    },
    fields: {
      DUPLICATE_IN_CHAIN: 'La mateixa marca apareix dues vegades a la cadena de correccions',
      UNKNOWN_WORKER: 'No es troba aquesta persona',
    },
  },
  en: {
    reasons: {
      SELF_REGISTER: 'You cannot record your own working day: clock in from your phone or ask another manager to record it',
      SELF_VALIDATE: 'You cannot sign off a day of yours with entries you recorded yourself: another manager has to sign it',
      SELF_VOID: 'You cannot void your own day: another manager has to do it',
      ORIGIN_TAKEN: 'That record from the other system was already imported',
      ORIGIN_CHANGED: 'That record from the other system was already imported with different data',
    },
    fields: {
      DUPLICATE_IN_CHAIN: 'The same mark appears twice in the chain of corrections',
      UNKNOWN_WORKER: 'That person could not be found',
    },
  },
  fr: {
    reasons: {
      SELF_REGISTER: 'Vous ne pouvez pas saisir votre propre journée : pointez depuis votre téléphone ou demandez à un autre responsable de la saisir',
      SELF_VALIDATE: 'Vous ne pouvez pas valider une de vos journées avec des entrées que vous avez saisies : un autre responsable doit la valider',
      SELF_VOID: 'Vous ne pouvez pas annuler votre propre journée : un autre responsable doit le faire',
      ORIGIN_TAKEN: 'Cet enregistrement de l’autre système a déjà été importé',
      ORIGIN_CHANGED: 'Cet enregistrement de l’autre système a déjà été importé avec d’autres données',
    },
    fields: {
      DUPLICATE_IN_CHAIN: 'La même marque apparaît deux fois dans la chaîne de corrections',
      UNKNOWN_WORKER: 'Cette personne est introuvable',
    },
  },
}

/** The module's catalogue with these hung from `errors`, locale by locale. */
export function withErrorReasons(base) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    const own = errorReasons[locale] ?? {}
    merged[locale] = {
      ...catalogue,
      errors: {
        ...catalogue.errors,
        reasons: { ...catalogue.errors?.reasons, ...own.reasons },
        fields: { ...catalogue.errors?.fields, ...own.fields },
      },
    }
  }
  return merged
}

export default errorReasons
